import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { ListItem } from '@rneui/base'
import { Icon, Text, makeStyles, useTheme } from '@rneui/themed'
import { useQueryClient } from '@tanstack/react-query'
import {
  QueryErrors,
  useConceptQueryErrorContext,
} from 'app/concept_query_error_context'
import { t } from 'app/i18l'
import { HomeStackParamList } from 'home/stack/types'
import { isEmpty } from 'lodash'
import { useEffect, useLayoutEffect, useState } from 'react'
import { Modal, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { RoundedButton } from './components/rounded-button'
import { showHeaderButton } from './header/header_button'
import { onManualRetry } from './queries/utils'

export function QueryErrorsIconModal(): React.JSX.Element {
  const { queryErrors } = useConceptQueryErrorContext()
  const navigation = useNavigation<StackNavigationProp<HomeStackParamList>>()

  const { theme } = useTheme()
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const styles = useStyles()

  useLayoutEffect(() => {
    if (!isEmpty(queryErrors)) {
      showHeaderButton({
        navigation,
        position: 'right',
        useIcon: true,
        title: 'warning-outline',
        onPress: () => setErrorModalVisible(true),
        theme: theme,
      })
    } else {
      navigation.setOptions({
        headerRight: () => null,
      })
    }
  }, [navigation, queryErrors, theme])

  useEffect(() => {
    if (isEmpty(queryErrors)) {
      setErrorModalVisible(false)
    }
  }, [queryErrors])

  return (
    <Modal
      visible={errorModalVisible}
      style={styles.container}
      onRequestClose={() => setErrorModalVisible(false)}
      animationType="fade"
      transparent
      statusBarTranslucent
    >
      <View style={styles.modalBackDrop}>
        <View style={styles.modalCard}>
          <Icon
            name="close"
            type="ionicon"
            onPress={() => setErrorModalVisible(false)}
            containerStyle={styles.iconContainer}
            iconStyle={styles.icon}
          />
          <ErrorListView queryErrors={queryErrors} />
        </View>
      </View>
    </Modal>
  )
}

interface ErrorListViewProps {
  queryErrors: QueryErrors
}

function ErrorListView({ queryErrors }: ErrorListViewProps) {
  const client = useQueryClient()
  const styles = useStyles()
  const [isLoading, setLoading] = useState(false)

  const onButtonPress = async () => {
    setLoading(true)
    await onManualRetry(client)
    setLoading(false)
  }

  const ErrorItem = ({ item }: { item: string }) => {
    return (
      <ListItem containerStyle={styles.errorItemContainer} topDivider>
        <ListItem.Content>
          <ListItem.Title style={styles.text}>{item}</ListItem.Title>
          {queryErrors[item]?.message ? (
            <ListItem.Subtitle style={styles.text}>
              {queryErrors[item]?.message}
            </ListItem.Subtitle>
          ) : null}
        </ListItem.Content>
      </ListItem>
    )
  }
  return (
    <View style={styles.errorListViewContainer}>
      <Text style={styles.errorListTitle}>{t('errorModalTitle')}</Text>
      <View style={styles.buttonContainer}>
        <RoundedButton
          title={`${t('retry', { locale: 'en' })} / ${t('retry', {
            locale: 'es',
          })}`}
          onPress={onButtonPress}
          loading={isLoading}
          emphasized
        />
      </View>
      <View style={styles.flatListContainer}>
        <FlatList
          data={Object.keys(queryErrors)}
          renderItem={ErrorItem}
          onTouchStart={e => e.stopPropagation()}
          onTouchMove={e => e.stopPropagation()}
        />
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,.5)',
  },
  errorListViewContainer: { paddingBottom: 30 },
  errorItemContainer: {
    alignSelf: 'center',
    backgroundColor: theme.colors.white,
  },
  icon: { padding: 10 },
  iconContainer: { alignSelf: 'flex-end' },
  modalBackDrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.50)',
    justifyContent: 'center',
  },
  text: { color: theme.colors.black },
  modalCard: {
    margin: 50,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  errorListTitle: { textAlign: 'center', fontSize: 18, paddingHorizontal: 20 },
  flatListContainer: { maxHeight: 400 },
  buttonContainer: { paddingVertical: 30, paddingHorizontal: 50 },
}))
